import React, { useState, useEffect, useCallback } from 'react';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { resolveUploadUrl } from '../api';
import { 
  Bell, 
  Heart, 
  UserPlus, 
  MessageCircle, 
  Repeat2, 
  Loader2, 
  CheckCheck,
  Trash2,
  X,
  AtSign,
  Mail,
  TrendingUp,
  CalendarClock,
  Scale
} from 'lucide-react';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const fetchNotifications = useCallback(async (isRefresh = false) => {
    if (!currentUser) return;
    try {
      if (!isRefresh) setLoading(true);
      const response = await api.get('/notifications/', { params: { limit: 100 } }); 
      
      // ✅ DEBUG: Check what data is coming from backend
      console.log("Fetched Notifications:", response.data);
      
      setNotifications(response.data || []);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const containerRef = usePullToRefresh(() => fetchNotifications(true));

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (notifId) => {
    setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
    try { await api.post(`/notifications/${notifId}/read`); } catch (err) { console.error(err); }
  };

  const handleMarkAllRead = async () => {
    setProcessing(true);
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) { console.error(err); }
    finally { setProcessing(false); }
  };

  const handleDeleteNotification = async (eOrId, notifId) => {
    let idToDelete = notifId;
    if (typeof eOrId === 'object' && eOrId !== null && eOrId.stopPropagation) {
      eOrId.stopPropagation();
      idToDelete = notifId;
    } else if (!notifId) {
      idToDelete = eOrId;
    }
    if (!idToDelete) return;

    const previous = notifications;
    setNotifications(prev => prev.filter(n => n.id !== idToDelete));
    try { await api.delete(`/notifications/${idToDelete}`); } 
    catch (err) { setNotifications(previous); }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Delete ALL notifications?")) return;
    setProcessing(true);
    const previous = notifications;
    setNotifications([]);
    try { await api.post('/notifications/clear-all'); } 
    catch (err) { setNotifications(previous); }
    finally { setProcessing(false); }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'like': return <Heart className="text-red-500" size={20} />;
      case 'follow': return <UserPlus className="text-blue-500" size={20} />;
      case 'comment': return <MessageCircle className="text-green-500" size={20} />;
      case 'repost': return <Repeat2 className="text-purple-500" size={20} />;
      case 'mention': return <AtSign className="text-orange-500" size={20} />;
      case 'message': return <Mail className="text-teal-500" size={20} />;
      case 'market_move': return <TrendingUp className="text-sky-500" size={20} />;
      case 'market_event': return <CalendarClock className="text-violet-500" size={20} />;
      case 'market_bias': return <Scale className="text-amber-500" size={20} />;
      default: return <Bell className="text-gray-500" size={20} />;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try { return new Date(dateString).toLocaleTimeString(); } catch { return ''; }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try { return new Date(dateString).toLocaleDateString(); } catch { return ''; }
  };

  // ✅ UPDATED: Smarter Click Handler
  const handleNotificationClick = (notif) => {
    console.log("Clicked Notification:", notif); // DEBUG

    if (!notif.is_read) handleMarkAsRead(notif.id);

    const actorId = notif.actor_id;
    const postId = notif.post_id;
    const postAuthorId = notif.post_author_id;
    const type = notif.type;

    // 1. MESSAGE: Go to chat with actor
    if (type === 'message') {
      if (actorId) {
        console.log("Navigating to message with:", actorId);
        navigate(`/dashboard/messages/${actorId}`);
      }
      return;
    }

    // 1b. MARKET ALERTS (system notifications, no actor): go to the market terminal
    if (type === 'market_move' || type === 'market_event' || type === 'market_bias') {
      navigate(notif.url || '/dashboard/markets');
      return;
    }

    // 2. FOLLOW: Go to actor's profile
    if (type === 'follow') {
      if (actorId) {
        console.log("Navigating to profile:", actorId);
        navigate(`/dashboard/profile/${actorId}`);
      }
      return;
    }

    // 3. POST RELATED (Like, Comment, Repost, Mention): Go to the post
    if (['like', 'comment', 'repost', 'mention'].includes(type)) {
      if (postId) {
        console.log("Navigating to post:", postId);
        // Try to go to the profile of the post author, highlighting the post
        if (postAuthorId) {
           navigate(`/dashboard/profile/${postAuthorId}?postId=${postId}`);
        } else {
           // Fallback: go to community and scroll to post
           navigate(`/dashboard/community?postId=${postId}`);
        }
      } else {
        console.warn("Post ID missing for post notification");
      }
      return;
    }
  };

  // ✅ HELPER: Generate Action Text
  const getActionText = (type) => {
    switch (type) {
      case 'like': return 'liked your post';
      case 'follow': return 'started following you';
      case 'comment': return 'commented on your post';
      case 'repost': return 'reposted your post';
      case 'mention': return 'mentioned you in a post';
      case 'message': return 'sent you a message';
      default: return 'sent a notification';
    }
  };

  return (
    <div ref={containerRef} className="h-full bg-slate-50 dark:bg-gray-900 p-4 md:p-6 pb-24 max-w-2xl mx-auto overflow-y-auto overflow-x-hidden">
      
      {/* Sticky header */}
      <div className="sticky top-0 z-10 -mx-4 md:-mx-6 px-4 md:px-6 pt-8 md:pt-3 pb-3 mb-5 bg-slate-50/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Bell size={24} /> Notifications
          </h1>
          
          <div className="flex gap-2">
             {notifications.length > 0 && (
              <button onClick={handleClearAll} disabled={processing} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                <Trash2 size={14} /> Clear
              </button>
            )}
            {notifications.some(n => !n.is_read) && (
              <button onClick={handleMarkAllRead} disabled={processing} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                <CheckCheck size={14} /> Read
              </button>
            )}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
            <Bell size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 font-medium">No notifications</p>
          </div>
        ) : (
          notifications.map(notif => {
            const actorName = notif.actor_name || 'User';
            
            return (
              <div 
                key={notif.id}
                onClick={() => handleNotificationClick(notif)}
                className={`relative flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-all border group
                  ${notif.is_read 
                    ? 'bg-white dark:bg-gray-800/60 border-gray-100 dark:border-gray-700' 
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800'}
                  hover:shadow-md active:scale-[0.99]
                `}
              >
                {/* Delete Button */}
                <button 
                  onClick={(e) => handleDeleteNotification(e, notif.id)}
                  className="absolute top-3 right-3 p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100"
                >
                  <X size={16} />
                </button>

                {/* Icon */}
                <div className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 border dark:border-gray-600">
                  {getNotificationIcon(notif.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pr-6">
                  {notif.actor_id ? (
                    <p className="text-gray-800 dark:text-gray-100 font-medium text-sm leading-snug">
                      <span className="font-bold">{actorName}</span>
                      {' '} {getActionText(notif.type)}
                    </p>
                  ) : (
                    <p className="text-gray-800 dark:text-gray-100 font-bold text-sm leading-snug">
                      {notif.title || 'Market alert'}
                    </p>
                  )}

                  {/* ✅ Show "body" text if it exists (The small text) */}
                  {notif.body && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                      {notif.body}
                    </p>
                  )}

                  <p className="text-xs text-gray-400 mt-1.5">
                    {formatTime(notif.created_at)} • {formatDate(notif.created_at)}
                  </p>
                </div>

                {/* Avatar / System badge */}
                <div className="flex-shrink-0">
                   {notif.actor_avatar ? (
                    <img src={resolveUploadUrl(notif.actor_avatar)} alt={actorName} className="w-8 h-8 rounded-full object-cover" />
                  ) : notif.actor_id ? (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xs text-white font-bold">
                      {actorName?.[0]?.toUpperCase()}
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center text-white">
                      {notif.type === 'market_event' ? <CalendarClock size={14} /> : notif.type === 'market_bias' ? <Scale size={14} /> : <TrendingUp size={14} />}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}